/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler) {
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}


/* Urban Cowboy Vault Support Bot */
(function () {
  function initVaultSupportBot() {
    if (document.getElementById('uco-support-bot')) return;

    var css = document.createElement('style');
    css.id = 'uco-support-bot-styles';
    css.textContent = [
      '#uco-support-bot{position:fixed;right:22px;bottom:92px;z-index:2147482000;font-family:Arial,sans-serif;color:#f8f3e9}',
      '.uco-help-launch{display:flex;align-items:center;gap:9px;min-height:52px;padding:0 18px;border:1px solid #d09a52;border-radius:999px;background:#16110d;color:#fff;box-shadow:0 12px 34px rgba(0,0,0,.42);font-weight:900;letter-spacing:.08em;cursor:pointer}',
      '.uco-help-launch svg{width:23px;height:23px;fill:#e3a75a}',
      '.uco-help-panel{position:absolute;right:0;bottom:66px;width:min(390px,calc(100vw - 28px));height:min(590px,calc(100vh - 145px));display:flex;flex-direction:column;overflow:hidden;border:1px solid #9c6a36;border-radius:16px;background:#120f0c;box-shadow:0 24px 70px rgba(0,0,0,.62)}',
      '.uco-help-panel[hidden]{display:none}',
      '.uco-help-head{display:flex;align-items:center;justify-content:space-between;padding:18px 18px 15px;border-bottom:1px solid #3b2c20;background:linear-gradient(135deg,#261b13,#15100c)}',
      '.uco-help-brand{display:flex;align-items:center;gap:11px}',
      '.uco-help-badge{display:grid;place-items:center;width:39px;height:39px;border:1px solid #d09a52;border-radius:50%;color:#e3a75a;font-size:21px;font-weight:900}',
      '.uco-help-title{margin:0;color:#f4d2a4;font-size:17px;font-weight:900;letter-spacing:.06em}',
      '.uco-help-status{margin:2px 0 0;color:#b9aa99;font-size:12px}',
      '.uco-help-close{border:0;background:transparent;color:#fff;font-size:28px;line-height:1;cursor:pointer}',
      '.uco-help-log{flex:1;overflow:auto;padding:17px;background:radial-gradient(circle at top right,rgba(117,74,37,.18),transparent 45%)}',
      '.uco-help-msg{max-width:88%;margin:0 0 11px;padding:11px 13px;border-radius:12px;font-size:14px;line-height:1.45;white-space:pre-line}',
      '.uco-help-msg.bot{border:1px solid #3c2d21;background:#211914;color:#f4eee7}',
      '.uco-help-msg.user{margin-left:auto;background:#b97935;color:#120c08;font-weight:700}',
      '.uco-help-msg a{color:#f1b66e;font-weight:800;text-underline-offset:3px}',
      '.uco-help-quick{display:flex;flex-wrap:wrap;gap:7px;padding:0 15px 13px;background:#120f0c}',
      '.uco-help-quick button{padding:8px 10px;border:1px solid #704a2b;border-radius:999px;background:#211812;color:#f2ddc2;font-size:12px;font-weight:800;cursor:pointer}',
      '.uco-help-form{display:flex;gap:8px;padding:12px;border-top:1px solid #3b2c20;background:#18120e}',
      '.uco-help-input{min-width:0;flex:1;padding:12px;border:1px solid #5b402b;border-radius:9px;background:#0d0b09;color:#fff;font-size:14px}',
      '.uco-help-send{padding:0 14px;border:0;border-radius:9px;background:#d08b42;color:#17100a;font-weight:900;cursor:pointer}',
      '.uco-help-note{margin:0;padding:0 12px 10px;background:#18120e;color:#82766b;font-size:10px;text-align:center}',
      '@media(max-width:600px){#uco-support-bot{right:12px;bottom:86px}.uco-help-launch{min-height:48px;padding:0 15px}.uco-help-panel{position:fixed;right:10px;bottom:144px;width:calc(100vw - 20px);height:min(600px,calc(100vh - 165px))}}',
      '@media(prefers-reduced-motion:no-preference){.uco-help-panel{animation:ucoHelpIn .18s ease-out}@keyframes ucoHelpIn{from{opacity:0;transform:translateY(10px) scale(.98)}to{opacity:1;transform:none}}}'
    ].join('');

    var root = document.createElement('div');
    root.id = 'uco-support-bot';
    root.innerHTML =
      '<button class="uco-help-launch" type="button" aria-expanded="false" aria-controls="uco-help-panel">' +
        '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 3h16a2 2 0 0 1 2 2v11a2 2 0 0 1-2 2H9l-5 4v-4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2Zm3 6v2h2V9H7Zm4 0v2h2V9h-2Zm4 0v2h2V9h-2Z"/></svg>' +
        '<span>NEED HELP?</span>' +
      '</button>' +
      '<section id="uco-help-panel" class="uco-help-panel" role="dialog" aria-modal="false" aria-label="Urban Cowboy support" hidden>' +
        '<header class="uco-help-head"><div class="uco-help-brand"><div class="uco-help-badge">UC</div><div><p class="uco-help-title">TRAIL GUIDE</p><p class="uco-help-status">Urban Cowboy Support</p></div></div><button class="uco-help-close" type="button" aria-label="Close support chat">&times;</button></header>' +
        '<div class="uco-help-log" aria-live="polite"></div>' +
        '<div class="uco-help-quick"></div>' +
        '<form class="uco-help-form"><label class="visually-hidden" for="uco-help-input">Ask a question</label><input id="uco-help-input" class="uco-help-input" autocomplete="off" placeholder="Ask me anything..." maxlength="180"><button class="uco-help-send" type="submit">SEND</button></form>' +
        '<p class="uco-help-note">Automated help. For account-specific support, contact our team.</p>' +
      '</section>';

    document.head.appendChild(css);
    document.body.appendChild(root);

    var launch = root.querySelector('.uco-help-launch');
    var panel = root.querySelector('.uco-help-panel');
    var close = root.querySelector('.uco-help-close');
    var log = root.querySelector('.uco-help-log');
    var quick = root.querySelector('.uco-help-quick');
    var form = root.querySelector('.uco-help-form');
    var input = root.querySelector('.uco-help-input');

    var topics = [
      ['Track my order', '/account'],
      ['Shipping', 'https://checkout.shopify.com/72057356353/policies/39615496257.html?locale=en'],
      ['Returns', 'https://forms.gle/64Kt7urRwrstCvRZ6'],
      ['Shop UCO discs', '/collections/uco-disc'],
      ['Become a wholesaler', 'https://forms.gle/PBwRwQcmVRMsdeF47'],
      ['Contact us', 'https://forms.gle/hprrgwidBpuVruN17']
    ];

    function addMessage(text, who, html) {
      var msg = document.createElement('div');
      msg.className = 'uco-help-msg ' + who;
      if (html) msg.innerHTML = text;
      else msg.textContent = text;
      log.appendChild(msg);
      log.scrollTop = log.scrollHeight;
    }

    function answer(raw) {
      var q = String(raw || '').toLowerCase();
      if (/track|where.*order|order status/.test(q)) {
        addMessage('Sign in to your account to view order details and tracking. If tracking has not updated, send us your order number through the contact page.<br><br><a href="/account">VIEW MY ACCOUNT</a> &nbsp; <a href="https://forms.gle/hprrgwidBpuVruN17">CONTACT SUPPORT</a>', 'bot', true);
      } else if (/ship|delivery|how long|arrive/.test(q)) {
        addMessage('Available shipping methods and current prices appear at checkout. Once your order ships, tracking is sent to the email used at checkout.<br><br><a href="https://checkout.shopify.com/72057356353/policies/39615496257.html?locale=en">VIEW SHIPPING POLICY</a>', 'bot', true);
      } else if (/return|refund|exchange|damaged|wrong item/.test(q)) {
        addMessage('For a return, damaged item, or incorrect order, contact our team with your order number and photos when relevant. We will review the request and explain the next step.<br><br><a href="https://forms.gle/64Kt7urRwrstCvRZ6">OPEN REFUND FORM</a> &nbsp; <a href="https://forms.gle/hprrgwidBpuVruN17">CONTACT SUPPORT</a>', 'bot', true);
      } else if (/wholesale|dealer|retail store|bulk/.test(q)) {
        addMessage('Interested in carrying Urban Cowboy Outdoors? Use our wholesale page or contact us with your store name, location, and expected order size.<br><br><a href="https://forms.gle/PBwRwQcmVRMsdeF47">BECOME A WHOLESALER</a> &nbsp; <a href="https://forms.gle/hprrgwidBpuVruN17">CONTACT US</a>', 'bot', true);
      } else if (/disc|flight|beginner|forehand|backhand|driver|mid|putter|recommend/.test(q)) {
        addMessage('I can point you in the right direction. Tell me your throwing style (forehand or backhand), approximate distance, and whether you want a straight, understable, or overstable flight.', 'bot');
      } else if (/contact|human|person|email|support/.test(q)) {
        addMessage('Our team can help with anything account-specific.<br><br><a href="https://forms.gle/hprrgwidBpuVruN17">OPEN CONTACT PAGE</a> &nbsp; <a href="mailto:rhoade4255@gmail.com">EMAIL SUPPORT</a>', 'bot', true);
      } else if (/hello|hey|hi|howdy/.test(q)) {
        addMessage('Howdy! Pick a topic below or ask me about an order, shipping, returns, wholesale, or choosing a disc.', 'bot');
      } else {
        addMessage('I do not want to steer you wrong. Try one of the topics below, or contact our team for a specific answer.<br><br><a href="https://forms.gle/hprrgwidBpuVruN17">CONTACT SUPPORT</a>', 'bot', true);
      }
    }

    topics.forEach(function (topic) {
      var button = document.createElement('button');
      button.type = 'button';
      button.textContent = topic[0];
      button.addEventListener('click', function () {
        window.location.href = topic[1];
      });
      quick.appendChild(button);
    });

    function setOpen(open) {
      panel.hidden = !open;
      launch.setAttribute('aria-expanded', String(open));
      if (open) {
        if (!log.children.length) addMessage('Howdy! I am the Trail Guide. I can help with orders, shipping, returns, wholesale, and finding the right disc.', 'bot');
        window.setTimeout(function () { input.focus(); }, 40);
      } else {
        launch.focus();
      }
    }

    launch.addEventListener('click', function () { setOpen(panel.hidden); });
    close.addEventListener('click', function () { setOpen(false); });
    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' && !panel.hidden) setOpen(false);
    });
    form.addEventListener('submit', function (event) {
      event.preventDefault();
      var value = input.value.trim();
      if (!value) return;
      addMessage(value, 'user');
      input.value = '';
      window.setTimeout(function () { answer(value); }, 180);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVaultSupportBot, { once: true });
  } else {
    initVaultSupportBot();
  }
})();


/* Urban Cowboy Packaging Add-ons */
(function () {
  var UCO_PACKAGING = [
    { id: 44361007595585, label: 'Protective bag', price: '$1.00' },
    { id: 44361005236289, label: 'Shipping box', price: '$1.00' },
    { id: 44361006645313, label: 'Cardboard backing', price: '$1.00' }
  ];
  var rendering = false;
  var busy = false;

  function installStyles() {
    if (document.getElementById('uco-packaging-styles')) return;
    var style = document.createElement('style');
    style.id = 'uco-packaging-styles';
    style.textContent = [
      '.uco-packaging{margin:0 0 16px;padding:16px;border:1px solid rgba(208,139,66,.55);border-radius:12px;background:#17110d;color:#f5eee6;text-align:left}',
      '.uco-packaging__title{margin:0 0 4px;color:#f2c58f;font-size:15px;font-weight:900;letter-spacing:.06em;text-transform:uppercase}',
      '.uco-packaging__sub{margin:0 0 12px;color:#bfb1a3;font-size:12px;line-height:1.4}',
      '.uco-packaging__option{display:flex;align-items:center;gap:10px;margin:8px 0;padding:10px;border:1px solid #493424;border-radius:9px;background:#211812;cursor:pointer}',
      '.uco-packaging__option:hover{border-color:#a8733d}',
      '.uco-packaging__option input{width:18px;height:18px;margin:0;accent-color:#d08b42;cursor:pointer}',
      '.uco-packaging__name{flex:1;color:#fff;font-size:13px;font-weight:800}',
      '.uco-packaging__price{color:#e3a75a;font-size:13px;font-weight:900}',
      '.uco-packaging.is-busy{opacity:.65;pointer-events:none}',
      '.uco-packaging__status{min-height:15px;margin:8px 0 0;color:#cbbba9;font-size:11px}',
      'cart-drawer .uco-packaging{margin:0 0 12px}',
      '@media(max-width:749px){.uco-packaging{padding:13px}.uco-packaging__option{padding:9px}}'
    ].join('');
    document.head.appendChild(style);
  }

  function getTargets() {
    return Array.from(document.querySelectorAll('.cart__ctas')).filter(function (node) {
      return !node.parentElement.querySelector(':scope > .uco-packaging');
    });
  }

  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (response) {
      if (!response.ok) throw new Error('Could not load cart');
      return response.json();
    });
  }

  function selectedIds(cart) {
    var selected = {};
    (cart.items || []).forEach(function (item) {
      if (UCO_PACKAGING.some(function (option) { return option.id === item.variant_id; })) {
        selected[item.variant_id] = true;
      }
    });
    return selected;
  }

  function buildPanel(cart) {
    var selected = selectedIds(cart);
    var panel = document.createElement('section');
    panel.className = 'uco-packaging';
    panel.setAttribute('aria-label', 'Optional packaging upgrades');
    panel.innerHTML =
      '<p class="uco-packaging__title">PACKAGING UPGRADES</p>' +
      '<p class="uco-packaging__sub">Add extra protection before checkout. Each selected option adds $1 to your order.</p>' +
      '<div class="uco-packaging__options"></div>' +
      '<p class="uco-packaging__status" aria-live="polite"></p>';

    var options = panel.querySelector('.uco-packaging__options');
    UCO_PACKAGING.forEach(function (option) {
      var label = document.createElement('label');
      label.className = 'uco-packaging__option';
      label.innerHTML =
        '<input type="checkbox" data-variant-id="' + option.id + '"' + (selected[option.id] ? ' checked' : '') + '>' +
        '<span class="uco-packaging__name">' + option.label + '</span>' +
        '<span class="uco-packaging__price">+' + option.price + '</span>';
      options.appendChild(label);
    });

    panel.addEventListener('change', function (event) {
      var checkbox = event.target.closest('input[data-variant-id]');
      if (!checkbox || busy) return;
      updateOption(panel, Number(checkbox.dataset.variantId), checkbox.checked);
    });
    return panel;
  }

  async function updateOption(panel, variantId, add) {
    busy = true;
    panel.classList.add('is-busy');
    var status = panel.querySelector('.uco-packaging__status');
    status.textContent = add ? 'Adding packaging upgrade…' : 'Removing packaging upgrade…';

    try {
      var endpoint = add ? '/cart/add.js' : '/cart/change.js';
      var payload = add
        ? { items: [{ id: variantId, quantity: 1 }] }
        : { id: variantId, quantity: 0 };
      var response = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error('Cart update failed');
      status.textContent = add ? 'Upgrade added.' : 'Upgrade removed.';
      try {
        await refreshDawnCartUI();
      } catch (error) {
        window.location.reload();
        return;
      }
      window.setTimeout(renderPanels, 150);
    } catch (error) {
      status.textContent = 'Could not update the cart. Please try again.';
      var checkbox = panel.querySelector('input[data-variant-id="' + variantId + '"]');
      if (checkbox) checkbox.checked = !add;
    } finally {
      busy = false;
      panel.classList.remove('is-busy');
    }
  }

  async function renderPanels() {
    if (rendering || busy) return;
    var targets = getTargets();
    if (!targets.length) return;
    rendering = true;
    try {
      var cart = await getCart();
      targets.forEach(function (target) {
        target.parentElement.insertBefore(buildPanel(cart), target);
      });
    } catch (error) {
      console.error('[UCO] Packaging add-ons could not load.', error);
    } finally {
      rendering = false;
    }
  }

  function initPackagingAddons() {
    installStyles();
    renderPanels();
    var observer = new MutationObserver(function () {
      window.clearTimeout(window.ucoPackagingRenderTimer);
      window.ucoPackagingRenderTimer = window.setTimeout(renderPanels, 120);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    document.addEventListener('shopify:section:load', renderPanels);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPackagingAddons, { once: true });
  } else {
    initPackagingAddons();
  }
})();
